import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import styles from "./LazyBackground.module.scss";

const LazyBackground = ({ src, alt, className, isFirst }) => {
  // Wenn isFirst true ist, sofort sichtbar machen
  const [isVisible, setIsVisible] = useState(!!isFirst);
  const [loaded, setLoaded] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!!isFirst) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "100px" }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [isFirst]);

  const handleImgLoad = () => {
    setLoaded(true);
  };

  // Hilfsfunktion für dynamische Media-Queries; erwartet numerische widths.
  const getMediaQuery = (i, arr) => {
    if (i === 0) {
      return `(max-width: ${arr[i].width}px)`;
    } else if (i < arr.length - 1) {
      return `(min-width: ${arr[i - 1].width + 1}px) and (max-width: ${arr[i].width}px)`;
    } else {
      return `(min-width: ${arr[i - 1].width + 1}px)`;
    }
  };

  if (typeof src === "object" && src !== null) {
    // Zuerst alle Childs umwandeln: width als int
    const childrenWithIntWidth = (src.childs || []).map(child => ({
      ...child,
      width: parseInt(child.width, 10)
    }));
    // Sortiere Childs aufsteigend nach width (kleinste zuerst)
    const sortedChildren = childrenWithIntWidth.sort((a, b) => a.width - b.width);
    return (
      <div
        ref={ref}
        className={`${styles.LazyBackground} ${className || ""} ${loaded ? styles.loaded : ""}`}
      >
        <div className={styles.backgroundContainer}>
          {isVisible && (
            <picture>
              {sortedChildren.map((child, index) => (
                <source
                  key={index}
                  media={getMediaQuery(index, sortedChildren)}
                  srcSet={child.mediaUrl}  // immer setzen
                />
              ))}
              <img
                src={src.mediaUrl}  // immer gesetzt, damit onLoad feuert
                alt={alt || src.alt_text || ""}
                className={styles.backgroundImage}
                loading={isFirst ? "eager" : "lazy"} // Änderung: conditionales loading
                onLoad={handleImgLoad}
              />
            </picture>
          )}
        </div>
      </div>
    );
  }

  // Fallback für string-basierte src
  return (
    <div
      ref={ref}
      className={`${styles.LazyBackground} ${className || ""} ${loaded ? styles.loaded : ""}`}
      style={{
        backgroundImage: `url(${src})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      role="img"
      aria-label={alt}
    >
      {isVisible && (
        <img
          src={src}
          alt={alt}
          loading={isFirst ? "eager" : "lazy"} // Änderung: conditionales loading
          onLoad={handleImgLoad}
          style={{ display: "none" }}
        />
      )}
    </div>
  );
};

LazyBackground.propTypes = {
  src: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  alt: PropTypes.string,
  className: PropTypes.string,
  isFirst: PropTypes.bool, // Neuer Prop
};

export default LazyBackground;